// MapView.js — live fleet map: vehicle pins, breadcrumb trail, planned route.
import React from 'react';
import { T } from '../lib/crewTokens';
import { fmtFt } from '../lib/crewFormat';
import { CrewGlyph, TruckIcon, PhoneIcon, OTBadge, ScheduleChip } from '../atoms/CrewAtoms';

const CM = T;

function pathD(pts) { return pts.map((p, i) => `${i === 0 ? 'M' : 'L'} ${p.x} ${p.y}`).join(' '); }

function MapView({ rows, allCrews, yard, selected, onSelect, glyph }) {
  const [hov, setHov] = React.useState(null);
  const inSet = React.useMemo(() => new Set(rows.map((c) => c.id)), [rows]);
  const colorOf = (l) => (l === 'critical' ? CM.rust : l === 'watch' ? CM.warn : CM.green);
  // which crew's path to emphasize — only if it's still visible in the filtered set,
  // so filtering out the selected crew doesn't dim every pin with nothing focused.
  const focusId = (selected && selected.id) || hov;
  const focus = focusId && inSet.has(focusId) ? allCrews.find((c) => c.id === focusId) : null;
  const hovC = hov ? allCrews.find((c) => c.id === hov) : null;
  const flipTip = hovC && hovC.currentPos.y < 24;

  return (
    <div className="crew-card-surface" style={{ height: '100%', position: 'relative', overflow: 'hidden' }}>
      <div className="crew-map-mock" style={{ position: 'absolute', inset: 0 }}>
        {/* roads */}
        <div className="crew-map-road-h" style={{ top: '30%' }}></div>
        <div className="crew-map-road-h" style={{ top: '64%' }}></div>
        <div className="crew-map-road-v" style={{ left: '34%' }}></div>
        <div className="crew-map-road-v" style={{ left: '66%' }}></div>

        <svg viewBox="0 0 100 100" preserveAspectRatio="none" style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', overflow: 'visible' }}>
          {/* breadcrumb trails + planned routes */}
          {allCrews.map((c) => {
            if (!inSet.has(c.id)) return null;
            const isFocus = focus && focus.id === c.id;
            const dim = focus && !isFocus;
            const col = colorOf(c.status.level);
            return (
              <g key={c.id} opacity={dim ? 0.12 : 1} style={{ transition: 'opacity 0.18s' }}>
                {/* breadcrumb (done) */}
                <path d={pathD(c.breadcrumb)} fill="none" stroke={col} strokeWidth={isFocus ? 0.9 : 0.5}
                  strokeLinecap="round" strokeLinejoin="round" strokeDasharray="0.2 1.6" opacity={isFocus ? 0.95 : 0.55}
                  className={isFocus ? 'crew-crumb-anim' : ''} vectorEffect="non-scaling-stroke" style={{ strokeWidth: isFocus ? 2.4 : 1.4 }} />
                {/* planned route ahead */}
                {c.plannedStops.length > 0 && (
                  <path d={pathD([c.currentPos, ...c.plannedStops])} fill="none" stroke={col}
                    strokeDasharray="2 2" opacity={isFocus ? 0.8 : 0.3} vectorEffect="non-scaling-stroke"
                    style={{ strokeWidth: isFocus ? 1.6 : 1 }} />
                )}
                {/* planned stop rings */}
                {isFocus && c.plannedStops.map((s, i) => (
                  <circle key={i} cx={s.x} cy={s.y} r="1.1" fill={CM.surface} stroke={col} vectorEffect="non-scaling-stroke" style={{ strokeWidth: 1.6 }} />
                ))}
              </g>
            );
          })}
        </svg>

        {/* yard */}
        <div style={{ position: 'absolute', left: `${yard.x}%`, top: `${yard.y}%`, transform: 'translate(-50%,-50%)', zIndex: 6, textAlign: 'center' }}>
          <div style={{ width: 13, height: 13, borderRadius: 4, background: '#0d1f14', border: '2px solid #fbf8f1', boxShadow: '0 1px 4px rgba(0,0,0,0.3)', margin: '0 auto' }}></div>
          <div style={{ fontSize: 8, fontWeight: 800, letterSpacing: '0.08em', color: '#3d463a', fontFamily: CM.mono, marginTop: 2 }}>YARD</div>
        </div>

        {/* vehicle pins */}
        {allCrews.map((c) => {
          const active = inSet.has(c.id);
          if (!active) return null;
          const isSel = selected && selected.id === c.id;
          const col = colorOf(c.status.level);
          const dim = focus && focus.id !== c.id;
          return (
            <div key={c.id} className={`crew-veh-pin${isSel ? ' selected' : ''}`}
              onClick={() => onSelect(c)} onMouseEnter={() => setHov(c.id)} onMouseLeave={() => setHov(null)}
              style={{
                left: `${c.currentPos.x}%`, top: `${c.currentPos.y}%`, zIndex: isSel ? 30 : dim ? 8 : 14,
                opacity: dim ? 0.4 : 1,
              }}>
              <span className="crew-veh-pin-body" style={{ background: col, boxShadow: `0 2px 6px rgba(28,36,25,0.3), 0 0 0 2px ${CM.surface}` }}>
                <TruckIcon size={12} color="#fbf8f1" />
              </span>
              {c.activity === 'driving' && <span className="crew-veh-pulse" style={{ borderColor: col }}></span>}
            </div>
          );
        })}

        {/* focused crew: geofence ring + phone-vs-truck split */}
        {focus && inSet.has(focus.id) && (
          <React.Fragment>
            <div style={{ position: 'absolute', left: `${focus.currentPos.x}%`, top: `${focus.currentPos.y}%`, transform: 'translate(-50%,-50%)', width: 56, height: 56, borderRadius: '50%', border: `2px dashed ${focus.loc.flags ? CM.rust : CM.green}`, background: focus.loc.flags ? 'rgba(169,52,29,0.06)' : 'rgba(31,94,58,0.06)', zIndex: 9, pointerEvents: 'none' }}></div>
            <div style={{ position: 'absolute', left: `${focus.currentPos.x}%`, top: `calc(${focus.currentPos.y}% - 34px)`, transform: 'translateX(-50%)', zIndex: 9, pointerEvents: 'none', fontSize: 8.5, fontFamily: CM.mono, fontWeight: 700, color: focus.loc.flags ? CM.rust : CM.green, background: 'rgba(251,248,241,0.85)', borderRadius: 4, padding: '1px 5px', whiteSpace: 'nowrap' }}>{focus.loc.geofenceFt}ft geofence</div>
            {focus.loc.phoneSplitFlag && (
              <svg viewBox="0 0 100 100" preserveAspectRatio="none" style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', zIndex: 16, pointerEvents: 'none', overflow: 'visible' }}>
                <line x1={focus.currentPos.x} y1={focus.currentPos.y} x2={focus.loc.phonePos.x} y2={focus.loc.phonePos.y} stroke={CM.rust} strokeDasharray="2 1.4" vectorEffect="non-scaling-stroke" style={{ strokeWidth: 1.6 }} />
              </svg>
            )}
            {focus.loc.phonePos && (
              <div style={{ position: 'absolute', left: `${focus.loc.phonePos.x}%`, top: `${focus.loc.phonePos.y}%`, transform: 'translate(-50%,-50%)', zIndex: 19, pointerEvents: 'none' }}>
                <span style={{ width: 18, height: 18, borderRadius: '50% 50% 50% 2px', background: focus.loc.phoneSplitFlag ? CM.rust : CM.ink2, display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: `0 1px 4px rgba(0,0,0,0.3), 0 0 0 2px ${CM.surface}` }}>
                  <PhoneIcon size={9} color="#fbf8f1" />
                </span>
                {focus.loc.phoneSplitFlag && (
                  <span style={{ position: 'absolute', top: '100%', left: '50%', transform: 'translateX(-50%)', marginTop: 3, fontSize: 8.5, fontFamily: CM.mono, fontWeight: 700, color: CM.rust, whiteSpace: 'nowrap', background: 'rgba(251,248,241,0.9)', borderRadius: 4, padding: '1px 4px' }}>phone {fmtFt(focus.loc.phoneSplitFt)} off</span>
                )}
              </div>
            )}
          </React.Fragment>
        )}

        {/* tooltip */}
        {hovC && (
          <div style={{
            position: 'absolute', left: `min(max(${hovC.currentPos.x}%, 120px), calc(100% - 120px))`, top: `${hovC.currentPos.y}%`,
            transform: flipTip ? 'translate(-50%, 20px)' : 'translate(-50%, calc(-100% - 18px))',
            zIndex: 40, pointerEvents: 'none', width: 228,
            background: 'rgba(251,248,241,0.98)', border: `1px solid ${CM.line2}`, borderRadius: 10,
            boxShadow: '0 4px 16px rgba(28,36,25,0.18)', padding: '9px 11px',
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 3 }}>
              <CrewGlyph level={hovC.status.level} variant={glyph} size={16} />
              <span style={{ fontSize: 12, fontWeight: 800, color: CM.ink, flex: 1 }}>{hovC.name}</span>
              <span style={{ fontSize: 9.5, fontWeight: 700, color: CM.mute, fontFamily: CM.mono }}>{hovC.vehicle.no}</span>
            </div>
            <div style={{ fontSize: 10.5, color: CM.ink2, fontWeight: 600, marginBottom: 5 }}>{hovC.location.label}</div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 5, flexWrap: 'wrap' }}>
              <OTBadge ot={hovC.ot} dense />
              <ScheduleChip behindMin={hovC.behindMin} dense />
              <span style={{ marginLeft: 'auto', fontFamily: CM.mono, fontSize: 10, fontWeight: 700, color: CM.mute }}>{hovC.ticketsDone}/{hovC.ticketsScheduled} ✓</span>
            </div>
          </div>
        )}

        {/* legend */}
        <div style={{ position: 'absolute', left: 12, bottom: 12, zIndex: 25, background: 'rgba(251,248,241,0.94)', backdropFilter: 'blur(8px)', border: `1px solid ${CM.line2}`, borderRadius: 9, boxShadow: '0 2px 8px rgba(28,36,25,0.1)', padding: '7px 11px', display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
          {[['healthy', 'Healthy'], ['watch', 'Watch'], ['critical', 'Critical']].map(([lvl, lbl]) => (
            <span key={lvl} style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 10.5, fontWeight: 700, color: CM.ink2 }}>
              <span style={{ width: 9, height: 9, borderRadius: 99, background: colorOf(lvl), border: '1.5px solid rgba(251,248,241,0.9)' }}></span>{lbl}
            </span>
          ))}
          <span style={{ fontSize: 9.5, color: CM.muteSoft, fontWeight: 600, borderLeft: `1px solid ${CM.line}`, paddingLeft: 10 }}>solid = trail · dashed = planned</span>
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 10.5, fontWeight: 700, color: CM.ink2 }}><span style={{ width: 12, height: 12, borderRadius: '50% 50% 50% 2px', background: CM.ink2, display: 'inline-block' }}></span>phone</span>
        </div>
        <div style={{ position: 'absolute', right: 12, bottom: 12, zIndex: 25, background: 'rgba(251,248,241,0.94)', border: `1px solid ${CM.line2}`, borderRadius: 6, padding: '2px 8px', fontFamily: CM.mono, fontSize: 9.5, fontWeight: 700, color: CM.mute }}>2 MI</div>
        <div style={{ position: 'absolute', left: 12, top: 12, zIndex: 25, fontSize: 10.5, color: CM.mute, fontWeight: 600, background: 'rgba(251,248,241,0.8)', borderRadius: 6, padding: '3px 8px' }}>Hover a truck to trace its route today</div>
      </div>
    </div>
  );
}

export { MapView };
